<script lang="ts" setup>
import { computed, ref } from "vue";
import BaseChart, {
  type ChartData,
  type ChartOptions,
} from "@/views/shared/components/BaseChart.vue";
import DashboardHeader from "@/components/DashboardHeader.vue";
import { useDashboardSummary } from "@/queries/dashboard";
import { STATUS_COLORS } from "./chartPalette";

// Diverging stacked bar: failures/warnings extend left (negative), successes right (positive).
// Pending is intentionally absent — RunHistory only holds completed runs, so an in-flight series
// would always be empty here; a live "running now" indicator belongs on the engine store instead.
const range = ref("auto");
const { summary, summaryLoading, summaryError } = useDashboardSummary(range);

// Show the busiest workflows first, capped so the bar stays readable.
const workflows = computed(() => summary.value.workflows.slice(0, 12));
const hasData = computed(() => workflows.value.length > 0);

const chartData = computed<ChartData>(() => ({
  labels: workflows.value.map((w) => w.name),
  datasets: [
    {
      label: "Warning",
      data: workflows.value.map((w) => -w.warning),
      backgroundColor: STATUS_COLORS.warning.bg,
      borderColor: STATUS_COLORS.warning.border,
      borderWidth: 1,
    },
    {
      label: "Failed",
      data: workflows.value.map((w) => -w.failed),
      backgroundColor: STATUS_COLORS.failed.bg,
      borderColor: STATUS_COLORS.failed.border,
      borderWidth: 1,
    },
    {
      label: "Success",
      data: workflows.value.map((w) => w.succeeded),
      backgroundColor: STATUS_COLORS.success.bg,
      borderColor: STATUS_COLORS.success.border,
      borderWidth: 1,
    },
  ],
}));

const chartOptions = computed<ChartOptions>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: "y",
  plugins: {
    legend: { display: true, position: "top" },
    title: { display: false },
    tooltip: {
      callbacks: {
        title: (context: any) => context[0].label,
        label: (context: any) =>
          `${context.dataset.label}: ${Math.abs(context.parsed.x)}`,
      },
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: true },
      ticks: { callback: (value: any) => Math.abs(value) },
    },
    y: { stacked: true, grid: { display: false } },
  },
}));
</script>

<template>
  <DashboardHeader :title="'Workflow Status'" />

  <div style="height: 300px; position: relative">
    <div v-if="summaryLoading && !hasData" class="text-muted py-5 text-center">
      Loading…
    </div>
    <div v-else-if="summaryError" class="text-danger py-5 text-center">
      Couldn't load workflow status.
    </div>
    <div v-else-if="!hasData" class="text-muted py-5 text-center">
      No runs yet. Status will appear here once your workflows have run.
    </div>
    <BaseChart
      v-else
      type="bar"
      :data="chartData"
      :options="chartOptions"
      :height="300"
    />
  </div>
</template>

<style scoped lang="scss"></style>
